// FILE: apps/admin/src/pages/BanAppeals.tsx
// VERSION: 1.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Ban Appeals queue page with responsive mobile-cards layout allowing operators to inspect user appeal arguments and approve (unban), reject, or request additional info with automatic Telegram DM notification.
//   SCOPE: BanAppeals component.
//   DEPENDS: ../api.js, ../components/AttachmentGallery.js, ../components/Icons.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   BanAppeals - Admin Ban Appeals management screen
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { api, dt, num } from '../api.js';
import {
  AttachmentCountBadge,
  AttachmentGallery,
  type MediaAttachmentItem,
} from '../components/AttachmentGallery.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

interface AdminBanAppealDto {
  id: string;
  userId: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  banReason: string | null;
  appealText: string;
  state: 'OPEN' | 'APPROVED' | 'REJECTED' | 'INFO_REQUESTED';
  resolutionNote: string | null;
  createdAt: string;
  attachments?: MediaAttachmentItem[];
}

interface AppealsResponse {
  items: AdminBanAppealDto[];
  pendingCount: number;
}

const STATES = ['ALL', 'OPEN', 'INFO_REQUESTED', 'APPROVED', 'REJECTED'] as const;

// START_CONTRACT: BanAppeals
//   PURPOSE: Render the Ban Appeals operator queue and resolution modal.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/bans/appeals and POST /admin/bans/appeals/:id/resolve
//   LINKS: M-ADMIN
// END_CONTRACT: BanAppeals
export function BanAppeals() {
  // START_BLOCK_BAN_APPEALS
  const [stateFilter, setStateFilter] = useState<string>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['ban-appeals', stateFilter],
    queryFn: () =>
      api.get<AppealsResponse>(
        `/admin/bans/appeals?state=${encodeURIComponent(stateFilter)}`,
      ),
    refetchInterval: 15000,
  });

  const selected = (data?.items ?? []).find((ap) => ap.id === selectedId) ?? null;

  const resolveMut = useMutation({
    mutationFn: ({
      id,
      status,
      resolutionNote,
    }: {
      id: string;
      status: 'approved' | 'rejected' | 'info_requested';
      resolutionNote: string;
    }) =>
      api.post(`/admin/bans/appeals/${id}/resolve`, {
        status,
        resolutionNote,
      }),
    onSuccess: () => {
      toast('Вердикт по апелляции сохранён и отправлен игроку в Telegram');
      setSelectedId(null);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['ban-appeals'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка вынесения вердикта'),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Апелляции блокировок (/appeal)</h1>
          <div className="page-subtitle">
            Очередь обращений заблокированных игроков · Ожидают рассмотрения:{' '}
            <strong>{num(data?.pendingCount ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          {STATES.map((st) => (
            <button
              key={st}
              type="button"
              className={`tab-btn ${stateFilter === st ? 'active' : ''}`}
              onClick={() => setStateFilter(st)}
            >
              {st === 'ALL' ? 'Все' : st}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="muted">Загрузка апелляций…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Причина бана</th>
                  <th>Текст апелляции</th>
                  <th>Статус</th>
                  <th>Подана</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((ap) => (
                  <tr key={ap.id}>
                    <td data-label="Игрок">
                      <button
                        type="button"
                        onClick={() => navigate(`/users?userId=${encodeURIComponent(ap.userId)}`)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          color: 'var(--accent)',
                          fontWeight: 600,
                          textAlign: 'left',
                        }}
                      >
                        {ap.firstName ?? 'Игрок'}{' '}
                        {ap.username ? `@${ap.username}` : `(#${ap.telegramId})`}
                      </button>
                    </td>
                    <td data-label="Причина бана">{ap.banReason ?? '—'}</td>
                    <td data-label="Текст апелляции" style={{ maxWidth: 340 }}>
                      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
                        {ap.appealText}
                      </div>
                      <AttachmentCountBadge attachments={ap.attachments} />
                      {ap.resolutionNote && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 4, overflowWrap: 'anywhere' }}>
                          Ответ оператора: {ap.resolutionNote}
                        </div>
                      )}
                    </td>
                    <td data-label="Статус">
                      <span className={`chip ${ap.state}`}>{ap.state}</span>
                    </td>
                    <td className="muted" data-label="Подана">{dt(ap.createdAt)}</td>
                    <td className="mobile-card-actions" data-label="">
                      <button
                        type="button"
                        className="btn sm primary"
                        onClick={() => {
                          setSelectedId(ap.id);
                          setNote(ap.resolutionNote ?? '');
                        }}
                      >
                        Рассмотреть
                      </button>
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Апелляций в выбранном статусе нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected &&
        (typeof document !== 'undefined'
          ? createPortal(
              <div className="modal-backdrop" onClick={() => setSelectedId(null)}>
                <div
                  className="modal-card"
                  style={{ maxWidth: 'min(560px, 100%)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 style={{ marginTop: 0, overflowWrap: 'anywhere' }}>
                    Апелляция от {selected.firstName ?? 'Игрока'}{' '}
                    {selected.username ? `@${selected.username}` : `#${selected.telegramId}`}
                  </h2>
                  <div className="card" style={{ background: 'var(--bg-2)', marginBottom: 12 }}>
                    <div className="muted" style={{ fontSize: 11 }}>Причина блокировки:</div>
                    <div style={{ fontWeight: 600, marginBottom: 8, overflowWrap: 'anywhere' }}>
                      {selected.banReason ?? 'Не указана'}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>Обращение игрока:</div>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: 4 }}>
                      {selected.appealText}
                    </div>
                  </div>

                  <AttachmentGallery
                    attachments={selected.attachments}
                    title={`Прикреплённые фото (${selected.attachments?.length ?? 0})`}
                  />

                  <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                    Комментарий оператора (будет отправлен игроку в Telegram):
                  </label>
                  <textarea
                    rows={3}
                    style={{ width: '100%', minWidth: 0 }}
                    placeholder="Напишите причину решения или вопрос для уточнения…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />

                  <div
                    className="row"
                    style={{ marginTop: 18, justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}
                  >
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn success"
                        disabled={resolveMut.isPending}
                        onClick={() =>
                          resolveMut.mutate({
                            id: selected.id,
                            status: 'approved',
                            resolutionNote:
                              note.trim() || 'Апелляция одобрена, блокировка снята.',
                          })
                        }
                      >
                        <AdminIcon name="check" size={13} />
                        <span>Одобрить и разбанить</span>
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={resolveMut.isPending}
                        onClick={() =>
                          resolveMut.mutate({
                            id: selected.id,
                            status: 'info_requested',
                            resolutionNote:
                              note.trim() ||
                              'Пожалуйста, предоставьте дополнительные пояснения.',
                          })
                        }
                      >
                        Запросить инфо
                      </button>
                      <button
                        type="button"
                        className="btn danger"
                        disabled={resolveMut.isPending}
                        onClick={() =>
                          resolveMut.mutate({
                            id: selected.id,
                            status: 'rejected',
                            resolutionNote:
                              note.trim() || 'В удовлетворении апелляции отказано.',
                          })
                        }
                      >
                        <AdminIcon name="close" size={13} />
                        <span>Отклонить</span>
                      </button>
                    </div>
                    <button type="button" className="btn ghost" onClick={() => setSelectedId(null)}>
                      Закрыть
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            )
          : null)}
    </>
  );
  // END_BLOCK_BAN_APPEALS
}
